import React, { useState, useEffect, useRef } from 'react';
import { facilityAPI } from '../services/api';
import api from '../services/api';
import { Camera, Upload, X, CheckCircle, AlertTriangle, ChevronDown, ChevronUp, Trash2, Loader, Shield, Zap, Eye } from 'lucide-react';
import { toast } from 'react-toastify';

function ConfidenceBadge({ confidence }) {
  const styles = {
    high: { bg: '#F0FDF4', color: '#16A34A', label: 'High Confidence' },
    medium: { bg: '#FFFBEB', color: '#D97706', label: 'Medium Confidence' },
    low: { bg: '#FEF2F2', color: '#DC2626', label: 'Low Confidence' },
  };
  const s = styles[confidence] || styles.medium;
  return <span style={{ background: s.bg, color: s.color, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>{s.label}</span>;
}

function ConditionBadge({ condition }) {
  const styles = {
    Good: { bg: '#F0FDF4', color: '#16A34A' },
    Fair: { bg: '#FFFBEB', color: '#D97706' },
    Poor: { bg: '#FEF2F2', color: '#DC2626' },
    Unknown: { bg: '#F8FAFC', color: '#64748b' },
  };
  const s = styles[condition] || styles.Unknown;
  return <span style={{ background: s.bg, color: s.color, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>{condition}</span>;
}

function AnalysisResult({ analysis, onAddToSources, facilityId }) {
  const [expanded, setExpanded] = useState({
    standards: true,
    actions: true,
    permit: false,
    nameplate: false
  });

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  if (!analysis.equipment_identified) {
    return (
      <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 12, padding: 24, textAlign: 'center' }}>
        <AlertTriangle size={32} color="#DC2626" style={{ marginBottom: 12 }} />
        <div style={{ fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700, color: '#DC2626', marginBottom: 8 }}>Equipment Not Identified</div>
        <div style={{ fontSize: 13, color: '#374151' }}>{analysis.not_identified_reason || 'Could not identify equipment in this image. Try a clearer photo with better lighting.'}</div>
      </div>
    );
  }

  return (
    <div>
      {/* Equipment Summary */}
      <div style={{ background: '#0A1628', borderRadius: 12, padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: 20, fontWeight: 800, color: 'white', letterSpacing: -0.5, marginBottom: 4 }}>
              {analysis.equipment_type}
            </div>
            {analysis.equipment_subtype && (
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>{analysis.equipment_subtype}</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <ConfidenceBadge confidence={analysis.confidence} />
            <ConditionBadge condition={analysis.condition} />
          </div>
        </div>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.65, margin: 0 }}>{analysis.description}</p>

        {/* Key specs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 10, marginTop: 14 }}>
          {analysis.estimated_capacity && (
            <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Capacity</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'white' }}>{analysis.estimated_capacity}</div>
            </div>
          )}
          {analysis.fuel_type && (
            <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Fuel</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'white' }}>{analysis.fuel_type}</div>
            </div>
          )}
          {analysis.manufacturer && analysis.manufacturer !== 'Not visible' && (
            <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Manufacturer</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'white' }}>{analysis.manufacturer}</div>
            </div>
          )}
        </div>
      </div>

      {/* Add to Sources button */}
      {analysis.emission_sources && analysis.emission_sources.length > 0 && facilityId && (
        <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '14px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1E40AF', marginBottom: 2 }}>Add to Sources</div>
            <div style={{ fontSize: 12, color: '#3B82F6' }}>This equipment can be added to your facility sources for tracking</div>
          </div>
          <button
            onClick={() => onAddToSources(analysis.emission_sources[0])}
            style={{ background: '#0066FF', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap' }}>
            Add Source
          </button>
        </div>
      )}

      {/* Compliance Concerns */}
      {analysis.compliance_concerns && analysis.compliance_concerns.length > 0 && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#DC2626', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <AlertTriangle size={14} /> Compliance Concerns
          </div>
          {analysis.compliance_concerns.map((concern, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
              <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#DC2626', marginTop: 6, flexShrink: 0 }} />
              <div style={{ fontSize: 13, color: '#374151' }}>{concern}</div>
            </div>
          ))}
        </div>
      )}

      {/* Likely Applicable Standards */}
      {analysis.likely_applicable_standards && analysis.likely_applicable_standards.length > 0 && (
        <div style={{ border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
          <div onClick={() => toggle('standards')} style={{ padding: '12px 16px', background: '#F8FAFC', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0A1628', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Shield size={14} color="#0066FF" /> Likely Applicable Standards ({analysis.likely_applicable_standards.length})
            </div>
            {expanded.standards ? <ChevronUp size={14} color="#94a3b8" /> : <ChevronDown size={14} color="#94a3b8" />}
          </div>
          {expanded.standards && (
            <div style={{ padding: '12px 16px' }}>
              {analysis.likely_applicable_standards.map((std, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, paddingBottom: 10, borderBottom: i < analysis.likely_applicable_standards.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                  <CheckCircle size={15} color="#16A34A" style={{ flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0A1628' }}>{std.standard}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{std.reason}</div>
                  </div>
                </div>
              ))}
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Run Federal Standards analysis for complete determination</div>
            </div>
          )}
        </div>
      )}

      {/* Recommended Actions */}
      {analysis.recommended_actions && analysis.recommended_actions.length > 0 && (
        <div style={{ border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
          <div onClick={() => toggle('actions')} style={{ padding: '12px 16px', background: '#F8FAFC', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0A1628', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Zap size={14} color="#D97706" /> Recommended Actions
            </div>
            {expanded.actions ? <ChevronUp size={14} color="#94a3b8" /> : <ChevronDown size={14} color="#94a3b8" />}
          </div>
          {expanded.actions && (
            <div style={{ padding: '12px 16px' }}>
              {analysis.recommended_actions.map((action, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#FFFBEB', border: '1px solid #FDE68A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#D97706', flexShrink: 0 }}>{i + 1}</div>
                  <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.55, paddingTop: 2 }}>{action}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Permit Considerations */}
      {analysis.permit_considerations && analysis.permit_considerations.length > 0 && (
        <div style={{ border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
          <div onClick={() => toggle('permit')} style={{ padding: '12px 16px', background: '#F8FAFC', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0A1628' }}>Permit Considerations</div>
            {expanded.permit ? <ChevronUp size={14} color="#94a3b8" /> : <ChevronDown size={14} color="#94a3b8" />}
          </div>
          {expanded.permit && (
            <div style={{ padding: '12px 16px' }}>
              {analysis.permit_considerations.map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#0066FF', marginTop: 6, flexShrink: 0 }} />
                  <div style={{ fontSize: 13, color: '#374151' }}>{c}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Nameplate Data */}
      {analysis.nameplate_data?.visible && (
        <div style={{ border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
          <div onClick={() => toggle('nameplate')} style={{ padding: '12px 16px', background: '#F8FAFC', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0A1628', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Eye size={14} color="#7C3AED" /> Nameplate Data Found
            </div>
            {expanded.nameplate ? <ChevronUp size={14} color="#94a3b8" /> : <ChevronDown size={14} color="#94a3b8" />}
          </div>
          {expanded.nameplate && (
            <div style={{ padding: '12px 16px' }}>
              <div style={{ fontSize: 13, color: '#374151', background: '#F5F3FF', borderRadius: 8, padding: '10px 12px' }}>
                {analysis.nameplate_data.data_found}
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{ background: '#F1F5F9', borderRadius: 8, padding: '10px 14px' }}>
        <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.55 }}>
          AI analysis based on visual inspection only. Results should be verified by a qualified engineer before use in regulatory submissions.
        </div>
      </div>
    </div>
  );
}

export default function PhotoAnalysis() {
  const [facilities, setFacilities] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [activeResult, setActiveResult] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState('');
  const [notes, setNotes] = useState('');
  const [preview, setPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [facilitiesRes, analysesRes] = await Promise.all([
        facilityAPI.getAll(),
        api.get('/photo-analysis')
      ]);
      setFacilities(facilitiesRes.data.facilities || []);
      setAnalyses(analysesRes.data.analyses || []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
    setActiveResult(null);
  };

  const handleAnalyze = async () => {
    if (!selectedFile) return toast.error('Please select a photo');
    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('photo', selectedFile);
      if (selectedFacility) formData.append('facilityId', selectedFacility);
      if (notes) formData.append('notes', notes);

      const res = await api.post('/photo-analysis/analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setActiveResult(res.data);
      setAnalyses(prev => [res.data.record, ...prev]);
      toast.success('Analysis complete');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAddToSources = async (sourceData) => {
    if (!selectedFacility) return toast.error('Select a facility first');
    try {
      await api.post('/sources', {
        facilityId: selectedFacility,
        name: sourceData.source_name,
        sourceType: sourceData.source_type,
        fuelType: sourceData.fuel_type,
        description: `Added via Photo Analysis on ${new Date().toLocaleDateString()}`
      });
      toast.success('Source added successfully');
    } catch (err) {
      toast.error('Failed to add source');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this analysis?')) return;
    try {
      await api.delete(`/photo-analysis/${id}`);
      setAnalyses(prev => prev.filter(a => a.id !== id));
      toast.success('Deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const clearPhoto = () => {
    setSelectedFile(null);
    setPreview(null);
    setActiveResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontFamily: 'var(--font-body)', outline: 'none', background: 'white', boxSizing: 'border-box' };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: '#64748b' }}>Loading...</div>;

  return (
    <div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } } @media (max-width: 768px) { .photo-grid { grid-template-columns: 1fr !important; } .photo-inner-grid { grid-template-columns: 1fr !important; } }`}</style>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Camera size={22} color="#0066FF" />
          Photo Analysis
        </div>
        <div className="page-subtitle">Photograph equipment and let AI identify it, assess compliance concerns, and determine applicable standards</div>
      </div>

      <div className="photo-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 24, alignItems: 'start' }}>

        {/* LEFT — Upload + History */}
        <div>
          {/* Upload Panel */}
          <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, padding: 24, marginBottom: 20 }}>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: 15, fontWeight: 700, color: '#0A1628', marginBottom: 16 }}>Upload Equipment Photo</div>

            {/* Photo area */}
            {!preview ? (
              <div
                style={{ border: '2px dashed #E2E8F0', borderRadius: 10, padding: 32, textAlign: 'center', marginBottom: 16, cursor: 'pointer', background: '#F8FAFC' }}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); handleFileSelect(e.dataTransfer.files[0]); }}>
                <Upload size={32} color="#94a3b8" style={{ marginBottom: 10 }} />
                <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Drop photo here or click to upload</div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>JPG, PNG, WebP up to 10MB</div>
              </div>
            ) : (
              <div style={{ position: 'relative', marginBottom: 16 }}>
                <img src={preview} alt="Equipment" style={{ width: '100%', borderRadius: 10, border: '1px solid #E2E8F0', maxHeight: 300, objectFit: 'cover' }} />
                <button
                  onClick={clearPhoto}
                  style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white' }}>
                  <X size={14} />
                </button>
              </div>
            )}

            {/* Camera button for mobile */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px', border: '1px solid #E2E8F0', borderRadius: 8, background: 'white', fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                <Upload size={14} /> Upload File
              </button>
              <button
                onClick={() => cameraInputRef.current?.click()}
                style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px', border: '1px solid #E2E8F0', borderRadius: 8, background: 'white', fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                <Camera size={14} /> Take Photo
              </button>
            </div>

            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFileSelect(e.target.files[0])} />
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => handleFileSelect(e.target.files[0])} />

            {/* Facility select */}
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Facility (optional)</label>
              <select style={inputStyle} value={selectedFacility} onChange={e => setSelectedFacility(e.target.value)}>
                <option value="">Select facility...</option>
                {facilities.map(f => <option key={f.id} value={f.id}>{f.facility_name || f.name}</option>)}
              </select>
            </div>

            {/* Notes */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Additional Notes (optional)</label>
              <input
                style={inputStyle}
                placeholder="e.g. Located in Building 3, rated 50 MMBtu/hr"
                value={notes}
                onChange={e => setNotes(e.target.value)} />
            </div>

            <button
              onClick={handleAnalyze}
              disabled={!selectedFile || analyzing}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: !selectedFile || analyzing ? '#94a3b8' : '#0066FF', color: 'white', border: 'none', borderRadius: 9, padding: '11px', fontSize: 14, fontWeight: 600, cursor: !selectedFile || analyzing ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-body)' }}>
              {analyzing
                ? <><Loader size={15} style={{ animation: 'spin 0.8s linear infinite' }} /> Analyzing...</>
                : <><Zap size={15} /> Analyze Equipment</>}
            </button>
          </div>

          {/* History */}
          <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #F1F5F9' }}>
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 14, fontWeight: 700, color: '#0A1628' }}>Previous Analyses</div>
            </div>
            {analyses.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No analyses yet</div>
            ) : (
              analyses.map(a => {
                const isActive = activeResult?.record?.id === a.id;
                return (
                  <div key={a.id}
                    onClick={() => {
                      const parsed = typeof a.analysis_result === 'string' ? JSON.parse(a.analysis_result) : a.analysis_result;
                      setActiveResult({ analysis: parsed, record: a });
                    }}
                    style={{ padding: '12px 20px', borderBottom: '1px solid #F8FAFC', cursor: 'pointer', background: isActive ? '#EFF6FF' : 'white', borderLeft: isActive ? '3px solid #0066FF' : '3px solid transparent' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0A1628' }}>{a.equipment_type || 'Unknown Equipment'}</div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                          {a.facility_name || 'No facility'} · {new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                      </div>
                      <button onClick={e => { e.stopPropagation(); handleDelete(a.id); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', padding: 4 }}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* RIGHT — Results */}
        <div>
          {analyzing && (
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, padding: 48, textAlign: 'center' }}>
              <Loader size={32} color="#0066FF" style={{ animation: 'spin 0.8s linear infinite', marginBottom: 16 }} />
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700, color: '#0A1628', marginBottom: 8 }}>Analyzing Equipment</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>AI is identifying equipment type, assessing compliance concerns, and determining applicable standards. This may take 15-20 seconds.</div>
            </div>
          )}

          {!analyzing && activeResult && (
            <AnalysisResult
              analysis={activeResult.analysis}
              facilityId={selectedFacility}
              onAddToSources={handleAddToSources} />
          )}

          {!analyzing && !activeResult && (
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, padding: 48, textAlign: 'center' }}>
              <Camera size={48} color="#E2E8F0" style={{ marginBottom: 16 }} />
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700, color: '#0A1628', marginBottom: 8 }}>Photo Analysis</div>
              <div style={{ fontSize: 13, color: '#64748b', maxWidth: 320, margin: '0 auto', lineHeight: 1.6 }}>
                Upload or take a photo of industrial equipment. AI will identify it, flag compliance concerns, and suggest applicable federal standards.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
